import { useEffect, useRef, useState, type ReactNode } from "react";
import { cx } from "../cx";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import type { QuoteCopy } from "../i18n/QuoteCopy";
import type { RequestPath } from "../types";
import { DocumentAnalysisPanel } from "./DocumentAnalysisPanel";
import { FillBadge } from "./fields";
import { ArrowLeftIcon, ArrowRightIcon, DocumentEditIcon, DocumentIcon, MailIcon, PaperclipIcon, SparklesIcon } from "./icons";
import { StepIndicator } from "./StepIndicator";

const DEMO_FIELD_IDS = [
  "loadCountry",
  "loadPostal",
  "loadCity",
  "unloadCountry",
  "unloadPostal",
  "unloadCity",
  "pickupDate",
] as const;

class ChooseQuickFillDemo {
  value(id: string, copy: QuoteCopy): string {
    if (id === "loadCountry" || id === "unloadCountry") return copy.countryName("NL");
    if (id === "loadPostal") return "3089 KN";
    if (id === "loadCity") return "Rotterdam";
    if (id === "unloadPostal") return "5928 PR";
    if (id === "unloadCity") return "Venlo";
    if (id === "pickupDate") return "14/10/2026";
    return "";
  }

  shipmentDetail(copy: QuoteCopy): string {
    const name = copy.palletLabel("euro").toLowerCase();
    const unit = copy.t("fill.palletMany");
    return `8 ${name} ${unit}, 4200 kg`;
  }
}

const demo = new ChooseQuickFillDemo();
const EMPTY_HIGHLIGHT: ReadonlySet<string> = new Set();
const DEMO_DROP_MS = 1420;
const DEMO_PROCESS_MS = 1800;
const DEMO_HOLD_MS = 1600;
const DEMO_REVEAL_START_MS = 80;
const DEMO_REVEAL_STEP_MS = 280;

type DemoPhase = "drop" | "process" | "fill";
type DemoSource = "email" | "pdf" | "screenshot";

class ChooseDemoCycle {
  static readonly sources: readonly DemoSource[] = ["email", "pdf", "screenshot"];

  constructor(private readonly index: number) {}

  kind(): DemoSource {
    return ChooseDemoCycle.sources[this.index % ChooseDemoCycle.sources.length];
  }

  nextIndex(): number {
    return (this.index + 1) % ChooseDemoCycle.sources.length;
  }
}

interface PreviewFieldProps {
  id: string;
  label: string;
  placeholder: string;
  filled: ReadonlySet<string>;
  highlighted: ReadonlySet<string>;
  copy: QuoteCopy;
  className?: string;
}

function PreviewField({ id, label, placeholder, filled, highlighted, copy, className }: PreviewFieldProps) {
  const isFilled = filled.has(id);
  const value = isFilled ? demo.value(id, copy) : "";
  return (
    <div className={cx("nv-field", isFilled && "nv-mark-filled", highlighted.has(id) && "flash", className)}>
      <div className="nv-label-row">
        <span className="nv-label">{label}</span>
        {isFilled && <FillBadge mark="filled" />}
      </div>
      <span className="nv-choose-preview-value">{value || placeholder}</span>
    </div>
  );
}

class ChoosePreviewStage {
  static readonly width = 600;
  static readonly height = 420;

  scale(width: number): number {
    return width <= 0 ? 1 : width / ChoosePreviewStage.width;
  }
}

const previewStage = new ChoosePreviewStage();

function ChoosePreviewFrame({ children }: { children: ReactNode }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const frame = frameRef.current;
    const stage = stageRef.current;
    if (!frame || !stage) return;
    const apply = () => {
      const scale = previewStage.scale(frame.clientWidth);
      frame.style.setProperty("--nv-choose-scale", String(scale));
      stage.style.setProperty("transform", `scale(${scale})`, "important");
    };
    const observer = new ResizeObserver(apply);
    observer.observe(frame);
    apply();
    return () => observer.disconnect();
  }, []);

  return (
    <div className="nv-choose-preview-well">
      <div className="nv-choose-preview-frame" ref={frameRef}>
        <div className="nv-choose-preview-stage" ref={stageRef}>
          {children}
        </div>
      </div>
    </div>
  );
}

function ChoosePreviewNav({
  continueLabel,
  continueIcon,
}: {
  continueLabel?: string;
  continueIcon?: ReactNode;
}) {
  const copy = useQuoteCopy();
  return (
    <div className="nv-choose-foot">
      <span className="nv-choose-foot-btn nv-choose-foot-back">
        <ArrowLeftIcon size={12} />
        {copy.t("nav.back")}
      </span>
      {continueLabel && (
        <span className="nv-choose-foot-btn nv-choose-foot-go">
          {continueIcon}
          {continueLabel}
        </span>
      )}
    </div>
  );
}

function ChooseRoutePreview({
  filled,
  highlighted,
  showShipment,
}: {
  filled: ReadonlySet<string>;
  highlighted: ReadonlySet<string>;
  showShipment?: boolean;
}) {
  const copy = useQuoteCopy();
  const field = (id: string, label: string, placeholder: string, className?: string) => (
    <PreviewField
      id={id}
      label={label}
      placeholder={placeholder}
      filled={filled}
      highlighted={highlighted}
      copy={copy}
      className={className}
    />
  );
  return (
    <div className="nv-choose-preview" aria-hidden="true">
      <StepIndicator steps={copy.stepLabels()} current={0} />
      <div className="nv-section">
        <h3 className="nv-section-title">{copy.t("page.routeSection")}</h3>
        <p className="nv-field-hint">{copy.t("route.hintPostcode")}</p>
        <div className="nv-route">
          <div className="nv-route-col">
            <div className="nv-route-tag">{copy.t("route.loading")}</div>
            {field("loadCountry", copy.t("route.country"), copy.t("route.selectCountry"))}
            {field("loadPostal", copy.t("route.postcode"), copy.t("route.postcodePlaceholder"))}
            {field("loadCity", copy.t("route.city"), copy.t("route.cityPlaceholder"))}
          </div>
          <div className="nv-route-col">
            <div className="nv-route-tag">{copy.t("route.unloading")}</div>
            {field("unloadCountry", copy.t("route.country"), copy.t("route.selectCountry"))}
            {field("unloadPostal", copy.t("route.postcode"), copy.t("route.postcodePlaceholder"))}
            {field("unloadCity", copy.t("route.city"), copy.t("route.cityPlaceholder"))}
          </div>
        </div>
      </div>
      <div className="nv-choose-date-row">
        {field("pickupDate", copy.t("route.pickupDate"), copy.t("route.datePlaceholder"), "nv-pickup")}
        {showShipment && (
          <p className="nv-shipment-filled">
            <span className="nv-shipment-filled-pill nv-anim">
              <SparklesIcon size={14} />
              {copy.t("fill.shipmentToo", { detail: demo.shipmentDetail(copy) })}
            </span>
          </p>
        )}
      </div>
      <ChoosePreviewNav continueLabel={copy.t("nav.continue")} continueIcon={<ArrowRightIcon size={12} />} />
    </div>
  );
}

function ChooseRequestMail() {
  return (
    <span className="nv-choose-mail">
      <span className="nv-choose-mail-head">
        <MailIcon size={14} />
        <span className="nv-choose-mail-title">Price request</span>
        <span className="nv-choose-mail-time">09:42</span>
      </span>
      <span className="nv-choose-mail-body">
        {`Hi, can you quote:
Loading 14/10 at 3089 KN Rotterdam
Unloading 15/10 at 5928 PR Venlo
8 pallets 120×80 cm, 4.200 kg total`}
      </span>
    </span>
  );
}

function ChooseRequestPdf() {
  return (
    <span className="nv-choose-doc nv-choose-doc-pdf">
      <span className="nv-choose-doc-file">
        <DocumentIcon size={22} />
        <span className="nv-choose-doc-ext">PDF</span>
      </span>
      <span className="nv-choose-doc-name">quote-request.pdf</span>
      <span className="nv-choose-doc-lines">
        {`Loading 3089 KN Rotterdam
Unloading 5928 PR Venlo
8 pallets · 4.200 kg`}
      </span>
    </span>
  );
}

function ChooseRequestScreenshot() {
  return (
    <span className="nv-choose-doc nv-choose-doc-shot">
      <span className="nv-choose-shot-canvas">
        <span className="nv-choose-shot-note">
          {`Hi, can you price this?
Loading Rotterdam 3089 KN, 14/10
Unloading Venlo 5928 PR, 15/10
8 pallets 120x80 and about 4200 kg`}
        </span>
      </span>
      <span className="nv-choose-shot-tag">Screenshot 2026-10-14.png</span>
    </span>
  );
}

function ChooseDemoAttachment({ source }: { source: DemoSource }) {
  if (source === "pdf") return <ChooseRequestPdf />;
  if (source === "screenshot") return <ChooseRequestScreenshot />;
  return <ChooseRequestMail />;
}

function ChooseQuickFillPaste({ source }: { source: DemoSource }) {
  const copy = useQuoteCopy();
  return (
    <div className="nv-choose-preview nv-choose-preview-paste" aria-hidden="true">
      <div className="nv-choose-qf-panel">
        <div className="qf-head">
          <span className="qf-badge">
            <SparklesIcon size={16} />
          </span>
          <span className="qf-title">{copy.t("page.quickFill")}</span>
        </div>
        <p className="qf-hint">{copy.t("qf.hint")}</p>
        <div className="paste-area">
          <span className="nv-choose-paste-ph">{copy.t("qf.placeholder")}</span>
          <span className="nv-choose-att">
            <span className="qf-doc-scan" />
            <ChooseDemoAttachment source={source} />
          </span>
          <span className="qf-attach-btn">
            <PaperclipIcon size={15} />
          </span>
        </div>
      </div>
      <span className="nv-choose-chip">
        <ChooseDemoAttachment source={source} />
      </span>
      <ChoosePreviewNav
        continueLabel={copy.t("qf.fillForm")}
        continueIcon={<SparklesIcon size={12} />}
      />
    </div>
  );
}

function ChooseQuickFillAnalyze({ source }: { source: DemoSource }) {
  return (
    <div className="nv-choose-preview nv-choose-preview-analyze" aria-hidden="true">
      <DocumentAnalysisPanel files={[]} preview={<ChooseDemoAttachment source={source} />} />
      <ChoosePreviewNav />
    </div>
  );
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function QuickFillPreview() {
  const [phase, setPhase] = useState<DemoPhase>("drop");
  const [revealed, setRevealed] = useState(0);
  const [sourceIndex, setSourceIndex] = useState(0);
  const source = new ChooseDemoCycle(sourceIndex).kind();

  useEffect(() => {
    if (phase !== "fill") {
      const wait = window.setTimeout(() => {
        if (phase === "drop") {
          setPhase("process");
          return;
        }
        setRevealed(prefersReducedMotion() ? DEMO_FIELD_IDS.length : 0);
        setPhase("fill");
      }, phase === "drop" ? DEMO_DROP_MS : DEMO_PROCESS_MS);
      return () => window.clearTimeout(wait);
    }
    if (revealed < DEMO_FIELD_IDS.length) {
      const step = window.setTimeout(
        () => setRevealed((current) => current + 1),
        revealed === 0 ? DEMO_REVEAL_START_MS : DEMO_REVEAL_STEP_MS,
      );
      return () => window.clearTimeout(step);
    }
    const hold = window.setTimeout(() => {
      setSourceIndex((current) => new ChooseDemoCycle(current).nextIndex());
      setPhase("drop");
    }, DEMO_HOLD_MS);
    return () => window.clearTimeout(hold);
  }, [phase, revealed]);

  const filled = new Set<string>(phase === "fill" ? DEMO_FIELD_IDS.slice(0, revealed) : []);
  const lastId = phase === "fill" && revealed > 0 && revealed < DEMO_FIELD_IDS.length ? DEMO_FIELD_IDS[revealed - 1] : undefined;
  const highlighted = lastId ? new Set([lastId]) : EMPTY_HIGHLIGHT;

  return (
    <ChoosePreviewFrame>
      {phase === "fill" ? (
        <ChooseRoutePreview
          filled={filled}
          highlighted={highlighted}
          showShipment={revealed >= DEMO_FIELD_IDS.length}
        />
      ) : phase === "process" ? (
        <ChooseQuickFillAnalyze source={source} />
      ) : (
        <ChooseQuickFillPaste source={source} />
      )}
    </ChoosePreviewFrame>
  );
}

interface Props {
  paths: readonly RequestPath[];
  onChoose: (path: RequestPath) => void;
}

export function ChoosePath({ paths, onChoose }: Props) {
  const copy = useQuoteCopy();
  const options = [
    {
      path: "manual" as const,
      title: copy.t("choose.manual.title"),
      action: copy.t("choose.manual.action"),
      tone: "manual" as const,
    },
    {
      path: "quickfill" as const,
      title: copy.t("choose.quickfill.title"),
      action: copy.t("choose.quickfill.action"),
      tone: "quickfill" as const,
    },
  ].filter((option) => paths.includes(option.path));
  return (
    <div className="nv-choose" data-step-heading tabIndex={-1}>
      <div className="nv-choose-grid">
        {options.map((option) => (
          <button
            key={option.path}
            type="button"
            className={`nv-choose-card nv-choose-card-${option.tone}`}
            onClick={() => onChoose(option.path)}
          >
            <span className="nv-choose-card-head">
              <span className="nv-choose-icon">
                {option.tone === "manual" ? <DocumentEditIcon size={22} /> : <SparklesIcon size={22} />}
              </span>
              <span className="nv-choose-card-title">{option.title}</span>
              <span className="nv-choose-go">
                <ArrowRightIcon size={16} />
              </span>
            </span>
            {option.path === "quickfill" ? (
              <QuickFillPreview />
            ) : (
              <ChoosePreviewFrame>
                <ChooseRoutePreview filled={EMPTY_HIGHLIGHT} highlighted={EMPTY_HIGHLIGHT} />
              </ChoosePreviewFrame>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
